import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@structural-climbing/auth'
import type { DeskPaymentMethod } from '../invoices/paymentMethod'
import type { Cart } from './types'

export type CartError = 'closed' | 'empty' | 'failed'

/** Thrown by the cart actions so the screen can say what went wrong. */
export class CartActionError extends Error {
  constructor(public readonly reason: CartError) {
    super(reason)
  }
}

function reasonFor(status: number, code: string | undefined): CartError {
  if (status === 409 || code === 'CART_NOT_OPEN') return 'closed'
  if (code === 'CART_EMPTY') return 'empty'
  return 'failed'
}

/**
 * Every open cart in the gym, and the actions on them.
 *
 * Carts live on the server, not in this hook: each change is written as it is
 * made, so a reload, a second device or a closed laptop loses nothing. The
 * list is read again whenever the window comes back into focus, which is what
 * keeps two desks roughly in step without a live connection.
 */
export function useCarts() {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [carts, setCarts] = useState<Cart[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  // Quantity writes still on their way. While any is, a refetch would bring
  // back the count from before the tap and the line would jump backwards.
  const inFlight = useRef(0)
  // One queue per cart line, so three fast taps arrive as 1, 2, 3 in order.
  const queues = useRef(new Map<string, Promise<void>>())

  const request = useCallback(
    async (path: string, init: RequestInit = {}) => {
      const token = await getAccessTokenSilently()
      return fetch(`${apiUrl}${path}`, {
        ...init,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      })
    },
    [apiUrl, getAccessTokenSilently],
  )

  const reload = useCallback(async () => {
    if (inFlight.current > 0) return
    try {
      const res = await request('/carts')
      if (!res.ok) {
        setError(true)
        return
      }
      const next = (await res.json()) as Cart[]
      // A tap may have landed while this was in the air; its write wins.
      if (inFlight.current === 0) setCarts(next)
      setError(false)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [request])

  useEffect(() => {
    void reload()
    const onFocus = () => {
      if (document.visibilityState === 'visible') void reload()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [reload])

  /** Opens a cart, or returns the one the member already has. */
  const openCart = useCallback(
    async (target: { userId: string } | { label: string }): Promise<Cart> => {
      const body = 'userId' in target ? { user_id: target.userId } : { label: target.label }
      const res = await request('/carts', { method: 'POST', body: JSON.stringify(body) })
      if (!res.ok) throw new CartActionError('failed')
      const cart = (await res.json()) as Cart
      setCarts((prev) => (prev.some((c) => c.id === cart.id) ? prev : [...prev, cart]))
      return cart
    },
    [request],
  )

  /**
   * Sets how many of a product a cart holds. The screen changes at once and
   * the write follows; if it fails the carts are read again, so the line goes
   * back to what the server really has.
   */
  const setQuantity = useCallback(
    (cartId: string, productId: string, quantity: number): Promise<void> => {
      setCarts((prev) =>
        prev.map((cart) => {
          if (cart.id !== cartId) return cart
          const rest = cart.items.filter((line) => line.product_id !== productId)
          const existing = cart.items.some((line) => line.product_id === productId)
          if (quantity <= 0) return { ...cart, items: rest }
          return {
            ...cart,
            items: existing
              ? cart.items.map((line) =>
                  line.product_id === productId ? { ...line, quantity } : line,
                )
              : [...rest, { product_id: productId, quantity }],
          }
        }),
      )

      const key = `${cartId}:${productId}`
      inFlight.current += 1
      const previous = queues.current.get(key) ?? Promise.resolve()
      const write = previous
        .catch(() => undefined)
        .then(async () => {
          const res = await request(`/carts/${cartId}/items`, {
            method: 'PUT',
            body: JSON.stringify({ product_id: productId, quantity }),
          })
          if (!res.ok) {
            const body = (await res.json().catch(() => ({}))) as { code?: string }
            throw new CartActionError(reasonFor(res.status, body.code))
          }
        })
        .finally(() => {
          inFlight.current -= 1
          if (queues.current.get(key) === write) queues.current.delete(key)
        })
      queues.current.set(key, write)

      return write.catch(async (e: unknown) => {
        await reload()
        throw e
      })
    },
    [request, reload],
  )

  const discardCart = useCallback(
    async (cartId: string) => {
      const res = await request(`/carts/${cartId}`, { method: 'DELETE' })
      // Already closed elsewhere is the outcome that was asked for.
      if (!res.ok && res.status !== 409) throw new CartActionError('failed')
      setCarts((prev) => prev.filter((cart) => cart.id !== cartId))
    },
    [request],
  )

  const sellCart = useCallback(
    async (cartId: string, paymentMethod: DeskPaymentMethod): Promise<number> => {
      // Every tap has to be on the server before it is asked to total the cart.
      await Promise.allSettled([...queues.current.values()])
      const res = await request(`/carts/${cartId}/sell`, {
        method: 'POST',
        body: JSON.stringify({ payment_method: paymentMethod }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { code?: string }
        const reason = reasonFor(res.status, body.code)
        // Sold or closed at another desk: it should leave this screen too.
        if (reason === 'closed') await reload()
        throw new CartActionError(reason)
      }
      const { total_amount } = (await res.json()) as { total_amount: number }
      setCarts((prev) => prev.filter((cart) => cart.id !== cartId))
      return total_amount
    },
    [request, reload],
  )

  /**
   * Moves a walk-in cart onto a member. When the member already has a cart the
   * lines are merged into it, so the cart that comes back may be a different one.
   */
  const assignCart = useCallback(
    async (cartId: string, userId: string): Promise<Cart> => {
      // The taps on this cart have to land before it is moved or merged.
      await Promise.allSettled(
        [...queues.current.entries()]
          .filter(([key]) => key.startsWith(`${cartId}:`))
          .map(([, write]) => write),
      )
      const res = await request(`/carts/${cartId}/assign`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { code?: string }
        const reason = reasonFor(res.status, body.code)
        if (reason === 'closed') await reload()
        throw new CartActionError(reason)
      }
      const cart = (await res.json()) as Cart
      setCarts((prev) => {
        const rest = prev.filter((c) => c.id !== cartId && c.id !== cart.id)
        return [...rest, cart]
      })
      return cart
    },
    [request, reload],
  )

  return {
    carts,
    loading,
    error,
    reload,
    openCart,
    setQuantity,
    discardCart,
    sellCart,
    assignCart,
  }
}
